<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { DataCleaningTypeGuardService } from '../services/DataCleaningTypeGuardService'
  import { RuleSchema } from '../models/RuleSchema'

  const props = defineProps<{
    visible: boolean
    rulesSchemas: Array<RuleSchema>
    configurationLevel: number
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'rule-selected': [rule: RuleSchema]
  }>()

  const availableRules = ref<RuleSchema[]>([])
  const filteredRules = ref<RuleSchema[]>([])
  const showHiddenRules = ref<boolean>(false)
  const loadingRules = ref<boolean>(false)
  const hasErrorLoadingRules = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group rules by category
  const groupedRules = ref<Record<string, RuleSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredRules.value = availableRules.value.filter((ruleSchema: RuleSchema) => {
        return (
          (ruleSchema.description && ruleSchema.description.toLowerCase().includes(search)) ||
          (ruleSchema.display_name && ruleSchema.display_name.toLowerCase().includes(search))
        )
      })
    } else {
      filteredRules.value = [...availableRules.value]
    }
    groupRulesByCategory()
  }

  const groupRulesByCategory = () => {
    const grouped: Record<string, RuleSchema[]> = {}
    // Filter out any rules that allow for selection of sub-rules if selecting a sub-rule
    if (props.configurationLevel !== 1) {
      filteredRules.value = filteredRules.value.filter(
        (x) => !DataCleaningTypeGuardService.supportsChildRules(x)
      )
    }
    filteredRules.value.forEach((rule) => {
      if (!grouped[rule.category]) {
        grouped[rule.category] = []
      }
      grouped[rule.category].push(rule)
    })
    groupedRules.value = grouped
  }

  const selectRule = (rule: RuleSchema) => {
    emit('rule-selected', rule)
    // Keep dialog open to allow adding multiple rules easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailableRules()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailableRules()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailableRules = () => {
    availableRules.value = props.rulesSchemas
    filteredRules.value = props.rulesSchemas
    groupRulesByCategory()
  }

  // Watch for visibility changes to load rules if needed
  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.rulesSchemas,
    (newItems) => {
      availableRules.value = [...newItems]
      filteredRules.value = [...newItems]
      groupRulesByCategory()
    }
  )

  defineExpose({
    availableRules
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Data Cleaning Rule"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="rule-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search rules by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox class="ml-2 flex-shrink-0 whitespace-nowrap" v-model:checked="showHiddenRules">
          Show All Rules
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingRules" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available rules...</p>
      </div>

      <!-- Rules List -->
      <div v-else id="rulesListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingRules"
          v-for="(rules, category) in groupedRules"
          :key="category"
          class="mb-6"
        >
          <!-- Only show category if it has rules (after filtering) -->
          <template
            v-if="rules.filter((x) => (showHiddenRules ? x : x.is_visible === true)).length > 0"
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ rules.length }} {{ rules.length === 1 ? 'rule' : 'rules' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="rule in rules.filter((x) => (showHiddenRules ? x : x.is_visible === true))"
                :key="rule.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectRule(rule)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ rule.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">{{ rule.description }}</p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ rule.fields.length }} field{{ rule.fields.length !== 1 ? 's' : '' }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingRules"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading rules.
        </div>
        <!-- No Results -->
        <div v-if="!hasErrorLoadingRules && filteredRules.length === 0" class="py-8 text-center">
          <p>
            {{ searchText.trim() ? 'No rules found matching your search.' : 'No rules available.' }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs">
            Try adjusting your search terms or clear the search to browse all rules.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">Click on any rule above to add it to your data cleaning pipeline</div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .rule-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  #rulesListContainer {
    flex: 1;
  }

  /* Custom scrollbar for rules list */
  #rulesListContainer::-webkit-scrollbar {
    width: 6px;
  }

  #rulesListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  #rulesListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  #rulesListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
